import React, { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Trash2, Eye } from "lucide-react";
import { adminApi, openWhatsApp } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_COLORS = {
  New: "bg-emerald-100 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-300",
  Contacted: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  Paid: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",
  Completed: "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
};
const STATUSES = ["New", "Contacted", "Paid", "Completed"];
const HIDDEN = new Set(["id", "status", "created_at", "full_name", "contact", "confirm"]);
const label = (k) => k.replace(/_/g, " ").replace(/\bcm\b/, "(cm)").replace(/\bkg\b/, "(kg)").replace(/^\w/, (c) => c.toUpperCase());

export const AdminBookings = ({ leads, setLeads }) => {
  const [view, setView] = useState(null);

  const cycleStatus = async (lead) => {
    const next = STATUSES[(STATUSES.indexOf(lead.status) + 1) % STATUSES.length];
    setLeads((ls) => ls.map((x) => (x.id === lead.id ? { ...x, status: next } : x)));
    try { await adminApi.patch(`/leads/${lead.id}/status`, { status: next }); } catch { toast.error("Update failed"); }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete booking from ${lead.full_name}?`)) return;
    try {
      await adminApi.delete(`/leads/${lead.id}`);
      setLeads((ls) => ls.filter((x) => x.id !== lead.id));
      toast.success("Booking deleted");
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="px-5 mt-5" data-testid="admin-bookings-section">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-base font-bold text-emerald-950 dark:text-emerald-50 font-head">Consultation Requests</h3>
        <span data-testid="admin-bookings-count" className="text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-800 px-2.5 py-1 rounded-full">{leads.length}</span>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">Private — visible to admin only. Contains personal & health information.</p>
      <div className="space-y-2.5">
        {leads.length === 0 && <p className="text-sm text-slate-400">No bookings yet.</p>}
        {leads.map((l) => (
          <div key={l.id} data-testid={`admin-lead-${l.id}`} className="p-3.5 rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-100 dark:border-emerald-800">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-bold text-emerald-950 dark:text-emerald-50">{l.full_name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{l.contact}{l.whatsapp && l.whatsapp !== l.contact ? ` · WA ${l.whatsapp}` : ""}</p>
                <p className="text-[11px] text-slate-400">{l.created_at ? new Date(l.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}</p>
              </div>
              <button data-testid={`admin-lead-status-${l.id}`} onClick={() => cycleStatus(l)} className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${STATUS_COLORS[l.status] || STATUS_COLORS.New}`}>{l.status}</button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {l.package_name && <Chip>{l.package_name}{l.package_price ? ` · ₹${l.package_price.toLocaleString("en-IN")}` : ""}</Chip>}
              {l.concern && <Chip>{l.concern}</Chip>}
              {l.bmi && <Chip>BMI {l.bmi}</Chip>}
              {l.start_date && <Chip>Start {l.start_date}</Chip>}
              {l.slot && <Chip>{l.slot}</Chip>}
            </div>
            <div className="mt-2.5 grid grid-cols-[1fr_auto_auto] gap-2">
              <button data-testid={`admin-lead-view-${l.id}`} onClick={() => setView(l)} className="flex items-center justify-center gap-1.5 bg-emerald-700 text-white text-xs font-bold py-2 rounded-xl"><Eye className="w-3.5 h-3.5" /> View details</button>
              <button data-testid={`admin-lead-wa-${l.id}`} onClick={() => openWhatsApp(`Hello ${l.full_name}, this is U&URS NUTRITIONIST regarding your ${l.package_name || "consultation"} request. 🌿`, l.whatsapp || l.contact)} className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-300 flex items-center justify-center"><MessageCircle className="w-4 h-4" /></button>
              <button data-testid={`admin-lead-delete-${l.id}`} onClick={() => remove(l)} className="w-9 h-9 rounded-xl bg-rose-50 dark:bg-rose-500/10 text-rose-600 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {view && (
        <Dialog open onOpenChange={(o) => !o && setView(null)}>
          <DialogContent className="max-w-[22rem] sm:max-w-md max-h-[85vh] overflow-y-auto rounded-3xl p-5" data-testid="lead-details-dialog">
            <DialogHeader><DialogTitle className="font-head text-emerald-950 dark:text-emerald-50">{view.full_name}</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500">{view.contact} · <span className={`font-bold ${STATUS_COLORS[view.status]?.split(" ")[1] || ""}`}>{view.status}</span></p>
            <div className="mt-2 rounded-2xl border border-slate-100 dark:border-emerald-800 divide-y divide-slate-100 dark:divide-emerald-800">
              {Object.entries(view).filter(([k, v]) => !HIDDEN.has(k) && v !== "" && v !== null && v !== undefined).map(([k, v]) => (
                <div key={k} className="px-3.5 py-2 text-sm">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label(k)}</p>
                  <p className="text-slate-800 dark:text-slate-100 whitespace-pre-wrap break-words">{typeof v === "number" ? v.toLocaleString("en-IN") : String(v)}</p>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const Chip = ({ children }) => (
  <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-emerald-800 px-2 py-0.5 rounded-full">{children}</span>
);
